import {
  createContext,
  type FormEvent,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  type ApprovalDetail,
  type ApprovalListResponse,
  type ApprovalRequestedPayload,
  type ArtifactMetadata,
  type AuditEventRecord,
  type AuditListResponse,
  type AutonomyMode,
  type Capability,
  type CapabilityCatalogResponse,
  ContractVersion,
  ContractVersionHeader,
  type ErrorResponse,
  type RunCancel,
  type RunCreate,
  type RunEventsResponse,
  type RunListResponse,
  type RunResponse,
  SourceBundleMediaType,
  type WorkspaceReadiness,
  type WorkspaceReadinessCheck,
  type WorkspaceConnectionsResponse,
  type WorkspaceOperatorsResponse,
  type WorkspaceReadinessConfigure,
  type WorkspaceSettingsResponse,
} from "@carbon-operations/contracts";
import {
  acknowledgedMutation,
  createIdempotencyKey,
  mutationApi,
  type MutationBoundary,
  readApi,
  readApiBytes,
  SessionEndedEvent,
} from "./api.js";
import { ApprovalActionContextFacts } from "./approval-action-context.js";
import { approvalActionContextDigestMatches } from "./approval-context-integrity.js";
import { approvalAccess, type ApprovalQueueRow, verifiedApprovalQueueRows } from "./approval-queue.js";
import { ApprovalQueueTable } from "./approval-queue-view.js";
import { RunListTable, runsInBoundary, runStates, stateLabel, stateTone } from "./run-list-view.js";
import { collectPages, createLatestLoader } from "./latest-load.js";
import {
  artifactEvidenceFrom,
  artifactIdFromRef,
  buildApprovalDecision,
  eventsMatchRunBoundary,
  readinessMatchesBoundary,
  verifiedApprovalRequestForRun,
  workspaceLocalMembership,
} from "./contract-builders.js";
import { ArtifactIntegrityCopy } from "./artifact-copy.js";
import {
  approvalMatchesBoundary,
  auditEventTypes,
  type AuditFilters,
  auditQuery,
  decisionUnavailableReason,
  policyEntries,
  recordsAccess,
  recordsMatchBoundary,
  verifiedApprovalDecisionEvidence,
} from "./workspace-records.js";
import {
  AuditLedger,
  ChainStatus,
  ConnectionRecords,
  OperatorDirectory,
} from "./workspace-records-view.js";
import {
  type ReadinessMutationLock,
  runSingleReadinessMutation,
  verifyQueuedReadiness,
} from "./readiness-verification.js";
import "./styles.css";

type SessionResponse = {
  schema: "carbon.operations.session/v1";
  identity: { issuer: string; subject_id: string; display_name?: string };
  memberships: Array<
    {
      tenant_id: string;
      workspace_ids: string[];
      roles: string[];
      scopes: string[];
      workspace_roles?: Record<string, string[]>;
    }
  >;
  expires_at: string;
  csrf_token: string;
};

type Membership = SessionResponse["memberships"][number];
type Runtime = {
  session: SessionResponse;
  membership: Membership;
  workspaceId: string;
  base: string;
  boundary: (idempotencyKey: string) => MutationBoundary;
};

const RuntimeContext = createContext<Runtime | null>(null);
const nav = [
  ["overview", "Overview", "01"],
  ["runs", "Runs", "02"],
  ["approvals", "Approvals", "03"],
  ["operators", "Operators", "04"],
  ["connections", "Connections", "05"],
  ["audit", "Audit", "06"],
  ["settings", "Settings", "07"],
] as const;

function useRuntime(): Runtime {
  const runtime = useContext(RuntimeContext);
  if (!runtime) throw new Error("Authenticated console context is unavailable");
  return runtime;
}

export function App() {
  return <SessionBootstrap />;
}

function SessionBootstrap() {
  const [state, setState] = useState<
    | "loading"
    | "signed_out"
    | "authorized"
    | "no_access"
    | "forbidden"
    | "expired"
    | "callback_failure"
    | "request_error"
  >("loading");
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [message, setMessage] = useState("");
  const wasAuthorized = useRef(false);

  async function loadSession() {
    setState("loading");
    setMessage("");
    try {
      const response = await fetch("/api/carbon-operations/v1/session", {
        credentials: "include",
        headers: {
          Accept: "application/json",
          [ContractVersionHeader]: ContractVersion,
        },
      });
      if (response.status === 401) {
        setSession(null);
        setState(wasAuthorized.current ? "expired" : "signed_out");
        return;
      }
      if (response.status === 403) {
        setSession(null);
        setState("forbidden");
        return;
      }
      if (!response.ok) {
        const error = await response.json().catch(() => null) as
          | ErrorResponse
          | null;
        setMessage(
          error?.message ?? `Session request failed (${response.status})`,
        );
        setState("request_error");
        return;
      }
      const payload = await response.json() as SessionResponse;
      setSession(payload);
      if (
        payload.memberships.length === 0 ||
        payload.memberships.every((item) => item.workspace_ids.length === 0)
      ) {
        setState("no_access");
        return;
      }
      wasAuthorized.current = true;
      setState("authorized");
    } catch (cause) {
      setMessage(
        cause instanceof Error
          ? cause.message
          : "The session service could not be reached",
      );
      setState("request_error");
    }
  }

  useEffect(() => {
    const query = new URLSearchParams(globalThis.location.search);
    if (query.get("auth") === "callback_failed") {
      setMessage("Identity verification did not complete.");
      setState("callback_failure");
      return;
    }
    void loadSession();
  }, []);

  useEffect(() => {
    const handleEndedSession = () => {
      setSession(null);
      setState("expired");
    };
    globalThis.addEventListener(SessionEndedEvent, handleEndedSession);
    return () =>
      globalThis.removeEventListener(SessionEndedEvent, handleEndedSession);
  }, []);

  if (state === "loading") {
    return (
      <AccessState
        eyebrow="Secure session"
        title="Checking your access"
        description="Confirming the same-origin session and authorized workspace memberships…"
      />
    );
  }
  if (state === "signed_out") {
    return (
      <AccessState
        eyebrow="Signed out"
        title="Sign in to OperationSV"
        description="Identity verification happens through the configured organization provider. This console never receives or stores an OIDC access or refresh token."
        action="Continue to sign in"
        onAction={() => startLogin(globalThis.location.pathname)}
      />
    );
  }
  if (state === "expired") {
    return (
      <AccessState
        eyebrow="Session ended"
        title="Your session expired or was revoked"
        description="No changes were submitted. Sign in again to refresh server-side membership and policy checks."
        action="Sign in again"
        onAction={() => startLogin(globalThis.location.pathname)}
      />
    );
  }
  if (state === "callback_failure") {
    return (
      <AccessState
        eyebrow="Identity callback failed"
        title="We could not complete sign-in"
        description={message}
        action="Restart sign-in"
        onAction={() => startLogin("/")}
        secondary="Return to signed-out view"
        onSecondary={() => {
          globalThis.history.replaceState({}, "", "/");
          setState("signed_out");
        }}
      />
    );
  }
  if (state === "request_error") {
    return (
      <AccessState
        eyebrow="Session unavailable"
        title="The control plane did not confirm access"
        description={message}
        action="Retry session check"
        onAction={() => void loadSession()}
      />
    );
  }
  if (state === "no_access") {
    return (
      <AccessState
        eyebrow="Authenticated · no access"
        title="No active workspace membership"
        description={`${
          session?.identity.display_name ?? session?.identity.subject_id ??
            "This identity"
        } is verified, but no authorized client workspace was returned. Changing the URL cannot grant access.`}
        action="Check again"
        onAction={() => void loadSession()}
      />
    );
  }
  if (state === "forbidden") {
    return (
      <AccessState
        eyebrow="Forbidden scope"
        title="This session cannot open the console"
        description="The control plane rejected the requested browser scope. No tenant or workspace data was exposed."
        action="Return to sign in"
        onAction={() => startLogin("/")}
      />
    );
  }
  if (!session) return null;
  return (
    <AuthorizedRoot
      session={session}
      onSessionEnded={() => {
        setSession(null);
        setState("signed_out");
      }}
    />
  );
}

function startLogin(returnTo: string) {
  globalThis.location.assign(
    `/auth/oidc/login?return_to=${
      encodeURIComponent(returnTo.startsWith("/") ? returnTo : "/")
    }`,
  );
}

function AccessState({
  eyebrow,
  title,
  description,
  action,
  onAction,
  secondary,
  onSecondary,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: string;
  onAction?: () => void;
  secondary?: string;
  onSecondary?: () => void;
}) {
  return (
    <main className="center-state">
      <span className="provisional-mark" aria-hidden="true">OSV</span>
      <div className="product-attribution">Carbon 6 by VLTRN</div>
      <div className="eyebrow">{eyebrow}</div>
      <h1>{title}</h1>
      <p>{description}</p>
      {action && (
        <button type="button" className="button primary" onClick={onAction}>
          {action}
        </button>
      )}
      {secondary && (
        <button
          type="button"
          className="text-button auth-secondary"
          onClick={onSecondary}
        >
          {secondary}
        </button>
      )}
    </main>
  );
}

function AuthorizedRoot(
  { session, onSessionEnded }: {
    session: SessionResponse;
    onSessionEnded: () => void;
  },
) {
  const options = useMemo(
    () =>
      session.memberships.flatMap((membership) =>
        membership.workspace_ids.map((workspaceId) => ({
          membership,
          workspaceId,
        }))
      ),
    [session],
  );
  const [selected, setSelected] = useState(() => {
    const currentPath = globalThis.location.pathname;
    const current = options.findIndex(({ membership, workspaceId }) =>
      currentPath.startsWith(`/t/${membership.tenant_id}/w/${workspaceId}/`)
    );
    return current >= 0 ? current : 0;
  });
  const active = options[selected] ?? options[0];
  if (!active) return null;
  const { workspaceId } = active;
  // Every page and the nav judge authority by this workspace's own roles, not the tenant-wide union.
  const membership = workspaceLocalMembership(active.membership, workspaceId);
  const runtime: Runtime = {
    session,
    membership,
    workspaceId,
    base: `/t/${membership.tenant_id}/w/${workspaceId}`,
    boundary: (idempotencyKey) => ({
      csrfToken: session.csrf_token,
      idempotencyKey,
      tenantId: membership.tenant_id,
    }),
  };
  return (
    <RuntimeContext.Provider value={runtime}>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={`${runtime.base}/overview`} replace />}
        />
        <Route
          path="/t/:tenantId/w/:workspaceId/*"
          element={
            <ScopedConsole
              options={options}
              selected={selected}
              onSelect={setSelected}
              onSessionEnded={onSessionEnded}
            />
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </RuntimeContext.Provider>
  );
}

function ScopedConsole({
  options,
  selected,
  onSelect,
  onSessionEnded,
}: {
  options: Array<{ membership: Membership; workspaceId: string }>;
  selected: number;
  onSelect: (index: number) => void;
  onSessionEnded: () => void;
}) {
  const runtime = useRuntime();
  const params = useParams();
  if (
    params["tenantId"] !== runtime.membership.tenant_id ||
    params["workspaceId"] !== runtime.workspaceId
  ) return <Unauthorized />;
  return (
    <Shell
      options={options}
      selected={selected}
      onSelect={onSelect}
      onSessionEnded={onSessionEnded}
    >
      <Routes>
        <Route path="overview" element={<Overview />} />
        <Route path="readiness" element={<Readiness />} />
        <Route path="runs" element={<RunsPage />} />
        <Route path="runs/new" element={<CreateRun />} />
        <Route path="runs/:runId" element={<RunDetail />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="approvals/:approvalId" element={<ApprovalDetailPage />} />
        <Route path="operators" element={<OperatorsPage />} />
        <Route path="connections" element={<ConnectionsPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

function Shell({
  children,
  options,
  selected,
  onSelect,
  onSessionEnded,
}: {
  children: ReactNode;
  options: Array<{ membership: Membership; workspaceId: string }>;
  selected: number;
  onSelect: (index: number) => void;
  onSessionEnded: () => void;
}) {
  const runtime = useRuntime();
  const canCreate = hasAnyRole(runtime.membership, [
    "owner",
    "admin",
    "operator",
  ]) && hasAllScopes(runtime.membership, [
    "capabilities:read",
    "readiness:read",
    "runs:create",
  ]);
  const canApprove = hasAnyRole(runtime.membership, [
    "owner",
    "admin",
    "approver",
  ]) && hasAllScopes(runtime.membership, ["approvals:decide"]);
  const canAdmin = hasAnyRole(runtime.membership, ["owner", "admin"]);
  const canAudit = hasAnyRole(runtime.membership, ["owner", "admin", "auditor"]);
  const visibleNav = nav.filter(([path]) =>
    path === "approvals"
      ? canApprove
      : path === "settings"
      ? canAdmin
      : path === "audit"
      ? canAudit
      : true
  );
  const [logoutState, setLogoutState] = useState<"idle" | "pending" | "error">(
    "idle",
  );
  const logoutKey = useRef(createIdempotencyKey());
  async function logout() {
    setLogoutState("pending");
    try {
      await acknowledgedMutation(
        "/session/logout",
        undefined,
        runtime.boundary(logoutKey.current),
      );
      onSessionEnded();
      globalThis.location.assign("/");
    } catch {
      setLogoutState("error");
    }
  }
  return (
    <div className="console-shell">
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <aside className="rail">
        <Link
          to={`${runtime.base}/overview`}
          className="brand"
          aria-label="OperationSV overview"
        >
          <span className="provisional-mark" aria-hidden="true">OSV</span>
          <span>
            <strong>OperationSV</strong>
            <small>Accountable operations control plane</small>
            <small className="brand-attribution">Carbon 6 by VLTRN</small>
          </span>
        </Link>
        <div className="scope-card">
          <small>Authorized workspace</small>
          <select
            aria-label="Authorized workspace"
            value={selected}
            onChange={(event) => {
              const next = Number(event.target.value);
              onSelect(next);
              const item = options[next];
              if (item) {
                globalThis.location.assign(
                  `/t/${item.membership.tenant_id}/w/${item.workspaceId}/overview`,
                );
              }
            }}
          >
            {options.map((item, index) => (
              <option
                key={`${item.membership.tenant_id}:${item.workspaceId}`}
                value={index}
              >
                {item.membership.tenant_id} / {item.workspaceId}
              </option>
            ))}
          </select>
        </div>
        <nav className="primary-nav" aria-label="Primary">
          {visibleNav.map(([path, label, index]) => (
            <NavLink
              key={path}
              to={`${runtime.base}/${path}`}
              className={({ isActive }) =>
                `nav-link ${isActive ? "active" : ""}`}
            >
              <span className="nav-index">{index}</span>
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="session-card">
          <span className="avatar" aria-hidden="true">
            {initials(
              runtime.session.identity.display_name ??
                runtime.session.identity.subject_id,
            )}
          </span>
          <span>
            <strong>
              {runtime.session.identity.display_name ??
                runtime.session.identity.subject_id}
            </strong>
            <small>{runtime.membership.roles.join(" · ") || "viewer"}</small>
          </span>
          <i aria-label="Session active" />
        </div>
        <button
          type="button"
          className="button ghost wide"
          disabled={logoutState === "pending"}
          onClick={() => void logout()}
        >
          {logoutState === "pending" ? "Signing out…" : "Sign out"}
        </button>
        {logoutState === "error" && (
          <span className="inline-error" role="alert">
            Session retained · sign-out failed
          </span>
        )}
      </aside>
      <div className="console-main">
        <header className="topbar">
          <div className="scope-breadcrumb">
            <span>{runtime.membership.tenant_id}</span>
            <b aria-hidden="true">/</b>
            <strong>{runtime.workspaceId}</strong>
          </div>
          <div className="top-actions">
            <span className="healthy">
              <i /> Same-origin session · scope checked
            </span>
            <Link className="button ghost" to={`${runtime.base}/readiness`}>
              Readiness
            </Link>
            {canCreate && (
              <Link className="button primary" to={`${runtime.base}/runs/new`}>
                + New run
              </Link>
            )}
          </div>
        </header>
        <main className="page" id="main-content">{children}</main>
      </div>
    </div>
  );
}

function initials(value: string) {
  return value.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase())
    .join("") || "CO";
}
function hasAnyRole(membership: Membership, allowed: string[]) {
  return membership.roles.some((role) => allowed.includes(role));
}
function hasAllScopes(membership: Membership, required: string[]) {
  return required.every((scope) => membership.scopes.includes(scope));
}
function supportsConsoleInput(capability: Capability) {
  if (
    capability.capability_id !== "ops.document.draft.v1" ||
    !capability.allowed_autonomy.includes("draft")
  ) return false;
  const properties = isRecord(capability.input_schema["properties"])
    ? capability.input_schema["properties"]
    : null;
  const source = properties && isRecord(properties["source_bundle"])
    ? properties["source_bundle"]
    : null;
  const sourceProperties = source && isRecord(source["properties"])
    ? source["properties"]
    : null;
  const media = sourceProperties && isRecord(sourceProperties["media_type"])
    ? sourceProperties["media_type"]
    : null;
  return media?.["const"] === SourceBundleMediaType;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function modeLabel(mode: AutonomyMode) {
  return mode === "observe"
    ? "Observe"
    : mode === "draft"
    ? "Draft"
    : "Act with approval";
}
function readinessLabel(key: WorkspaceReadinessCheck["key"]) {
  const labels: Record<WorkspaceReadinessCheck["key"], string> = {
    tenant_owner: "Tenant owner",
    workspace_profile: "Workspace purpose and data classification",
    provider_custodian: "Provider custodian",
    dedicated_runner: "Dedicated runner",
    event_sink: "Event sink",
    capability_policy: "Capability and approval policy",
    approval_group: "Approval group",
    budget_threshold: "Budget threshold",
    artifact_retention: "Artifact retention",
    object_store: "Object store",
    canary_probe: "Storage canary probe",
  };
  return labels[key];
}
function toLocalDateTimeValue(iso: string) {
  const date = new Date(iso);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}
function localDateTimeToIso(value: string): string | null {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds)
    ? new Date(milliseconds).toISOString()
    : null;
}
async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
}
function PageHeader(
  { eyebrow, title, description, action }: {
    eyebrow: string;
    title: string;
    description: string;
    action?: ReactNode;
  },
) {
  return (
    <div className="page-header">
      <div>
        <div className="eyebrow">{eyebrow}</div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="panel">
      <header>
        <h2>{title}</h2>
      </header>
      {children}
    </section>
  );
}
function Status(
  { children, tone }: {
    children: ReactNode;
    tone: "neutral" | "success" | "attention" | "danger";
  },
) {
  return (
    <span className={`status ${tone}`}>
      <i />
      {children}
    </span>
  );
}
function Definition({ term, value }: { term: string; value: string }) {
  return (
    <div className="definition">
      <dt>{term}</dt>
      <dd>{value}</dd>
    </div>
  );
}
function MutationNotice(
  { title, children, tone = "pending" }: {
    title: string;
    children: ReactNode;
    tone?: "pending" | "success" | "error";
  },
) {
  return (
    <div
      className={`mutation-notice ${tone}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <strong>{title}</strong>
      <span>{children}</span>
    </div>
  );
}

function Overview() {
  const runtime = useRuntime();
  const canCreate = hasAnyRole(runtime.membership, [
    "owner",
    "admin",
    "operator",
  ]) && hasAllScopes(runtime.membership, [
    "capabilities:read",
    "readiness:read",
    "runs:create",
  ]);
  return (
    <>
      <PageHeader
        eyebrow="Workspace / Operations overview"
        title={runtime.workspaceId}
        description="A fail-closed view of the selected client boundary and the v1 controls currently exposed by the server."
      />
      <section className="readiness-banner">
        <div>
          <strong>Authenticated operating boundary</strong>
          <p>
            Readiness, tenant, workspace, role and scopes come only from the
            server. Open the checklist before the first run.
          </p>
        </div>
        <Link className="button ghost" to={`${runtime.base}/readiness`}>
          Review readiness
        </Link>
      </section>
      <section className="metrics">
        <Metric
          label="Session expires"
          value={new Date(runtime.session.expires_at).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
          note="No refresh token retained"
        />
        <Metric
          label="Effective roles"
          value={String(runtime.membership.roles.length).padStart(2, "0")}
          note={runtime.membership.roles.join(" · ") || "None"}
        />
        <Metric
          label="Effective scopes"
          value={String(runtime.membership.scopes.length).padStart(2, "0")}
          note="Server rechecks every request"
        />
        <Metric
          label="Workspace"
          value="01"
          note="Selected from active membership"
        />
      </section>
      <Panel title="Primary accountable journey">
        <div className="prose">
          <p>{ArtifactIntegrityCopy.overviewEvidence}</p>
          {canCreate && (
            <Link className="button primary" to={`${runtime.base}/runs/new`}>
              Create bounded run
            </Link>
          )}
        </div>
      </Panel>
    </>
  );
}
function Metric(
  { label, value, note }: { label: string; value: string; note: string },
) {
  return (
    <article className="metric">
      <small>{label}</small>
      <strong>{value}</strong>
      <span>{note}</span>
    </article>
  );
}

function RunsPage() {
  const runtime = useRuntime();
  const canCreate = hasAnyRole(runtime.membership, [
    "owner",
    "admin",
    "operator",
  ]) && hasAllScopes(runtime.membership, [
    "capabilities:read",
    "readiness:read",
    "runs:create",
  ]);
  const canRead = hasAllScopes(runtime.membership, ["runs:read"]);
  const [filter, setFilter] = useState<RunResponse["state"] | "">("");
  const [runs, setRuns] = useState<RunResponse[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [error, setError] = useState("");
  const loader = useRef(createLatestLoader());
  async function load(after?: string) {
    const attempt = loader.current.begin();
    setLoadState("loading");
    try {
      const query = new URLSearchParams({
        workspace_id: runtime.workspaceId,
        limit: "50",
        ...(filter === "" ? {} : { state: filter }),
        ...(after === undefined ? {} : { cursor: after }),
      });
      const result = await readApi<RunListResponse>(`/runs?${query}`, {
        tenantId: runtime.membership.tenant_id,
        signal: attempt.signal,
      });
      if (!attempt.current()) return;
      const page = runsInBoundary(result.runs, {
        tenantId: runtime.membership.tenant_id,
        workspaceId: runtime.workspaceId,
      });
      setRuns((previous) => after === undefined ? page : [...previous, ...page]);
      setCursor(result.next_cursor);
      setLoadState("ready");
    } catch (cause) {
      if (!attempt.current()) return;
      setError(cause instanceof Error ? cause.message : "Runs unavailable");
      setLoadState("error");
    }
  }
  useEffect(() => {
    if (!canRead) return;
    const current = loader.current;
    setRuns([]);
    setCursor(undefined);
    void load();
    return () => current.cancel();
  }, [canRead, filter, runtime.membership.tenant_id, runtime.workspaceId]);
  return (
    <>
      <PageHeader
        eyebrow="Runs / Workspace scoped"
        title="Runs"
        description="Runs in this workspace, newest first. Open a run for its events, approval and artifacts."
        action={canCreate
          ? (
            <Link className="button primary" to={`${runtime.base}/runs/new`}>
              + New run
            </Link>
          )
          : null}
      />
      {!canRead
        ? (
          <Panel title="Runs need the runs:read scope">
            <div className="prose">
              <p>
                Your active membership does not include runs:read, so the run
                list is not available to you.
              </p>
            </div>
          </Panel>
        )
        : (
          <Panel title="Run ledger">
            <label className="field">
              <span>State</span>
              <select
                value={filter}
                onChange={(event) =>
                  setFilter(event.target.value as RunResponse["state"] | "")}
              >
                <option value="">All states</option>
                {runStates.map((state) => (
                  <option key={state} value={state}>{stateLabel(state)}</option>
                ))}
              </select>
            </label>
            {loadState === "error"
              ? (
                <MutationNotice tone="error" title="Runs unavailable">
                  {error}
                </MutationNotice>
              )
              : loadState === "loading" && runs.length === 0
              ? <p className="empty-state">Loading runs…</p>
              : <RunListTable runs={runs} base={runtime.base} />}
            {cursor !== undefined && loadState !== "error" && (
              <button
                type="button"
                className="button ghost"
                disabled={loadState === "loading"}
                onClick={() => void load(cursor)}
              >
                Load more
              </button>
            )}
          </Panel>
        )}
    </>
  );
}

function ApprovalsPage() {
  const runtime = useRuntime();
  // runtime.membership is workspace-local (AuthorizedRoot), so this judges this workspace's roles.
  const access = approvalAccess(runtime.membership);
  const mayDecide = access !== "none";
  const canReview = access === "review";
  const [rows, setRows] = useState<ApprovalQueueRow[]>([]);
  const [complete, setComplete] = useState(true);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [error, setError] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const loader = useRef(createLatestLoader());
  async function load() {
    const attempt = loader.current.begin();
    setLoadState("loading");
    try {
      const boundary = { tenantId: runtime.membership.tenant_id, signal: attempt.signal };
      const listed = await collectPages(async (cursor) => {
        const query = new URLSearchParams({
          state: "pending",
          workspace_id: runtime.workspaceId,
          limit: "100",
          ...(cursor === undefined ? {} : { cursor }),
        });
        const result = await readApi<ApprovalListResponse>(`/approvals?${query}`, boundary);
        return { items: result.approvals, next_cursor: result.next_cursor };
      }, 50);
      // Each row's facts come from its run's ordered approval.requested event, exactly as run detail reads them.
      const events = new Map(
        await Promise.all(listed.items.map(async (approval) => {
          try {
            const result = await readApi<RunEventsResponse>(
              `/runs/${encodeURIComponent(approval.run_id)}/events`,
              boundary,
            );
            return [approval.run_id, result.events] as const;
          } catch {
            return [approval.run_id, null] as const;
          }
        })),
      );
      if (!attempt.current()) return;
      const verifiedRows = await verifiedApprovalQueueRows(listed.items, events, {
        tenantId: runtime.membership.tenant_id,
        workspaceId: runtime.workspaceId,
      });
      if (!attempt.current()) return;
      setRows(verifiedRows);
      setComplete(listed.complete);
      setNowMs(Date.now());
      setLoadState("ready");
    } catch (cause) {
      if (!attempt.current()) return;
      setError(
        cause instanceof Error ? cause.message : "Pending approvals unavailable",
      );
      setLoadState("error");
    }
  }
  useEffect(() => {
    if (!canReview) return;
    const current = loader.current;
    setRows([]);
    void load();
    const tick = setInterval(() => setNowMs(Date.now()), 1_000);
    const refresh = setInterval(() => void load(), 30_000);
    return () => {
      clearInterval(tick);
      clearInterval(refresh);
      current.cancel();
    };
  }, [canReview, runtime.membership.tenant_id, runtime.workspaceId]);
  if (!canReview) {
    return (
      <>
        <PageHeader
          eyebrow="Governance / Human attention"
          title="Approvals"
          description="Pending exact-action approvals for this workspace."
        />
        <Panel title={mayDecide ? "Reviewing approvals needs runs:read" : "Approvals are decided by approvers"}>
          <div className="prose">
            <p>
              {mayDecide
                ? "You may decide approvals in this workspace, but reviewing one reads its run and ordered events, which needs the runs:read scope. Your membership does not include it; ask an owner to add runs:read."
                : "Only an owner, admin or approver of this workspace whose membership includes the approvals:decide scope can see and decide pending approvals. Your role in this workspace does not, so there is nothing to show here. Requests you create still appear on their run detail."}
            </p>
          </div>
        </Panel>
      </>
    );
  }
  return (
    <>
      <PageHeader
        eyebrow="Governance / Human attention"
        title="Approvals"
        description="Pending exact-action approvals, soonest expiry first. Open a run to review its exact action and decide."
        action={
          <button
            type="button"
            className="button ghost"
            onClick={() => void load()}
          >
            Refresh
          </button>
        }
      />
      <Panel title="Waiting for a decision">
        {loadState === "error"
          ? (
            <MutationNotice tone="error" title="Approvals unavailable">
              {error}
            </MutationNotice>
          )
          : loadState === "loading" && rows.length === 0
          ? <p className="empty-state">Loading pending approvals…</p>
          : (
            <>
              {!complete && (
                <MutationNotice tone="pending" title="More approvals available">
                  More pending approvals exist than the {rows.length} loaded.
                  The order above covers only the loaded ones; decide these and
                  refresh to load more.
                </MutationNotice>
              )}
              <ApprovalQueueTable
                rows={rows}
                base={runtime.base}
                nowMs={nowMs}
              />
            </>
          )}
      </Panel>
    </>
  );
}

function CreateRun() {
  const runtime = useRuntime();
  const canCreate = hasAnyRole(runtime.membership, [
    "owner",
    "admin",
    "operator",
  ]) && hasAllScopes(runtime.membership, [
    "capabilities:read",
    "readiness:read",
    "runs:create",
  ]);
  const navigate = useNavigate();
  const eventSinkId = import.meta.env["VITE_CARBON_EVENT_SINK_ID"]?.trim() as
    | string
    | undefined;
  const [catalog, setCatalog] = useState<Capability[]>([]);
  const [selected, setSelected] = useState("");
  const [autonomy, setAutonomy] = useState<AutonomyMode>("observe");
  const [goal, setGoal] = useState("");
  const [sourceRef, setSourceRef] = useState("");
  const [sourceSha256, setSourceSha256] = useState("");
  const [executionEnabled, setExecutionEnabled] = useState<boolean | null>(
    null,
  );
  const [providerCustody, setProviderCustody] = useState<
    | NonNullable<WorkspaceReadiness["configuration"]>["provider_custodian"]
    | null
  >(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [submitState, setSubmitState] = useState<"idle" | "pending" | "error">(
    "idle",
  );
  const [error, setError] = useState("");
  const createIntent = useRef<{
    key: string;
    request: RunCreate | null;
  }>({ key: createIdempotencyKey(), request: null });
  function beginNewCreateIntent() {
    createIntent.current = { key: createIdempotencyKey(), request: null };
    if (submitState === "error") setSubmitState("idle");
    setError("");
  }
  useEffect(() => {
    if (!canCreate) return;
    let active = true;
    void Promise.all([
      readApi<CapabilityCatalogResponse>("/capabilities", {
        tenantId: runtime.membership.tenant_id,
      }),
      readApi<WorkspaceReadiness>(
        `/workspaces/${encodeURIComponent(runtime.workspaceId)}/readiness`,
        { tenantId: runtime.membership.tenant_id },
      ),
    ]).then(([result, readiness]) => {
      if (!active) return;
      if (
        !readinessMatchesBoundary(readiness, {
          tenantId: runtime.membership.tenant_id,
          workspaceId: runtime.workspaceId,
        })
      ) throw new Error("Readiness response did not match the selected scope");
      setExecutionEnabled(readiness.execution_enabled);
      setProviderCustody(readiness.configuration?.provider_custodian ?? null);
      const enabled = result.capabilities.filter((item) => item.enabled);
      setCatalog(enabled);
      const first = enabled.find(supportsConsoleInput);
      if (first) {
        setSelected(first.capability_id);
        setAutonomy(first.allowed_autonomy[0]!);
      }
      setLoadState("ready");
    }).catch((cause) => {
      if (active) {
        setError(
          cause instanceof Error
            ? cause.message
            : "Capability catalog unavailable",
        );
        setLoadState("error");
      }
    });
    return () => {
      active = false;
    };
  }, [canCreate, runtime.membership.tenant_id, runtime.workspaceId]);
  const capability = catalog.find((item) => item.capability_id === selected);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!capability || !eventSinkId) return;
    setSubmitState("pending");
    setError("");
    const request: RunCreate = createIntent.current.request ?? {
      schema: "carbon.operations.run.create/v1",
      request_id: `request_${crypto.randomUUID()}`,
      tenant_id: runtime.membership.tenant_id,
      workspace_id: runtime.workspaceId,
      intent: "task_execution",
      capability_id: capability.capability_id,
      goal,
      inputs: capability.capability_id === "ops.document.draft.v1"
        ? [{
          name: "source_bundle",
          ref: sourceRef,
          media_type: SourceBundleMediaType,
          sha256: sourceSha256,
        }]
        : [],
      requested_autonomy: autonomy,
      on_behalf_of: {
        subject_id: runtime.session.identity.subject_id,
        subject_type: "user",
      },
      event_sink_id: eventSinkId,
      correlation_id: `correlation_${crypto.randomUUID()}`,
      expires_at: new Date(Date.now() + 60 * 60 * 1_000).toISOString(),
      metadata: { source: "console" },
    };
    createIntent.current.request = request;
    try {
      const result = await acknowledgedMutation(
        "/runs",
        request,
        runtime.boundary(createIntent.current.key),
      );
      if (!result.runId) {
        throw new Error("Control plane response omitted the run reference");
      }
      navigate(`${runtime.base}/runs/${result.runId}`);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "The run was not created",
      );
      setSubmitState("error");
    }
  }
  if (!canCreate) {
    return (
      <>
        <PageHeader
          eyebrow="Runs / Role boundary"
          title="Run creation forbidden"
          description="The active membership lacks an owner/admin/operator role or the required capabilities:read, readiness:read and runs:create scopes. No capability data was requested."
        />
        <MutationNotice tone="error" title="Run creation unavailable">
          No mutation is simulated and no success is shown without a
          control-plane acknowledgement.
        </MutationNotice>
      </>
    );
  }
  if (loadState === "loading") {
    return (
      <>
        <PageHeader
          eyebrow="Runs / Create"
          title="Loading enabled capabilities"
          description="Reading the workspace-scoped catalog from the control plane…"
        />
        <MutationNotice title="Catalog pending">
          No run can be submitted until the server confirms its enabled
          capability boundary.
        </MutationNotice>
      </>
    );
  }
  if (loadState === "error") {
    return (
      <>
        <PageHeader
          eyebrow="Runs / Create"
          title="Capability catalog unavailable"
          description="The control plane did not return an enabled catalog."
        />
        <MutationNotice tone="error" title="Run creation disabled">
          {error}
        </MutationNotice>
      </>
    );
  }
  return (
    <>
      <PageHeader
        eyebrow="Runs / Create"
        title="Create a bounded run"
        description="Authority comes from the server-returned capability catalog and authenticated membership—not this form."
      />
      <form className="creation-grid" onSubmit={submit}>
        <section>
          <h2>1 · Enabled capability</h2>
          <div className="capability-grid">
            {catalog.map((item) => (
              <label
                className={`capability-card ${
                  selected === item.capability_id ? "selected" : ""
                } ${supportsConsoleInput(item) ? "" : "unsupported"}`}
                key={item.capability_id}
              >
                <input
                  type="radio"
                  name="capability"
                  disabled={!supportsConsoleInput(item)}
                  checked={selected === item.capability_id}
                  onChange={() => {
                    beginNewCreateIntent();
                    setSelected(item.capability_id);
                    setAutonomy(item.allowed_autonomy[0]!);
                  }}
                />
                <span className="capability-title">{item.title}</span>
                <code>{item.capability_id}</code>
                <p>
                  Required scopes: {item.required_scopes.join(", ") || "none"}
                </p>
                {!supportsConsoleInput(item) && (
                  <p>Input controls are not available in this v1 console.</p>
                )}
                <span className="capability-footer">
                  <Status
                    tone={item.risk_class === "external_write"
                      ? "attention"
                      : "success"}
                  >
                    {stateLabel(item.risk_class)}
                  </Status>
                  <b>{item.allowed_autonomy.map(modeLabel).join(" · ")}</b>
                </span>
              </label>
            ))}
          </div>
          <h2>2 · Requested boundary</h2>
          {capability?.capability_id === "ops.document.draft.v1" && (
            <fieldset className="source-bundle">
              <legend>{ArtifactIntegrityCopy.sourceBundleLegend}</legend>
              <p>
                Enter only the opaque workspace artifact reference and its
                verified digest. Paths and external URLs are not accepted.
              </p>
              <label className="field">
                <span>Artifact reference</span>
                <input
                  required
                  type="text"
                  value={sourceRef}
                  pattern="artifact://[A-Za-z0-9][A-Za-z0-9_-]*"
                  placeholder="artifact://source_bundle_id"
                  onChange={(event) => {
                    beginNewCreateIntent();
                    setSourceRef(event.target.value.trim());
                  }}
                />
              </label>
              <label className="field">
                <span>SHA-256</span>
                <input
                  required
                  type="text"
                  value={sourceSha256}
                  pattern="[a-f0-9]{64}"
                  maxLength={64}
                  spellCheck={false}
                  placeholder="64 lowercase hexadecimal characters"
                  onChange={(event) => {
                    beginNewCreateIntent();
                    setSourceSha256(event.target.value.trim());
                  }}
                />
              </label>
              <Definition term="Media type" value={SourceBundleMediaType} />
            </fieldset>
          )}
          <label className="field">
            <span>Goal</span>
            <textarea
              required
              rows={5}
              maxLength={2000}
              value={goal}
              onChange={(event) => {
                beginNewCreateIntent();
                setGoal(event.target.value);
              }}
              placeholder="State the accountable outcome for this run"
            />
          </label>
          <label className="field">
            <span>Autonomy</span>
            <select
              value={autonomy}
              onChange={(event) => {
                beginNewCreateIntent();
                setAutonomy(event.target.value as AutonomyMode);
              }}
            >
              {capability?.allowed_autonomy.map((mode) => (
                <option key={mode} value={mode}>{modeLabel(mode)}</option>
              ))}
            </select>
          </label>
        </section>
        <aside className="review-card">
          <div className="eyebrow">Preflight review</div>
          <h2>{capability?.title ?? "No enabled capability"}</h2>
          <Definition term="Tenant" value={runtime.membership.tenant_id} />
          <Definition term="Workspace" value={runtime.workspaceId} />
          <Definition term="Autonomy" value={modeLabel(autonomy)} />
          <Definition
            term="Risk"
            value={capability
              ? stateLabel(capability.risk_class)
              : "Unavailable"}
          />
          <Definition
            term="Approval"
            value={autonomy === "act_with_approval"
              ? "Exact action digest required"
              : "Server policy decides after submission"}
          />
          <Definition
            term="Event sink"
            value={eventSinkId ?? "Not configured"}
          />
          <Definition
            term="Readiness gate"
            value={executionEnabled
              ? "Execution enabled"
              : "Execution disabled"}
          />
          <Definition
            term="Provider custodian"
            value={providerCustody
              ? `${providerCustody.provider_label} · ${providerCustody.subject_id}`
              : "Not configured"}
          />
          <Definition
            term="Custody scope"
            value={providerCustody?.scope ?? "Unavailable"}
          />
          <Definition
            term="Custody purpose"
            value={providerCustody?.purpose ?? "Unavailable"}
          />
          <Definition
            term="Custody review / expiry"
            value={providerCustody
              ? `${new Date(providerCustody.review_at).toLocaleString()} / ${
                new Date(providerCustody.expires_at).toLocaleString()
              }`
              : "Unavailable"}
          />
          {capability?.capability_id === "ops.document.draft.v1" && (
            <Definition
              term="Source custody"
              value="Workspace artifact · exact digest"
            />
          )}
          {!eventSinkId && (
            <MutationNotice tone="error" title="Run creation disabled">
              VITE_CARBON_EVENT_SINK_ID must name a server-authorized workspace
              event sink.
            </MutationNotice>
          )}
          {executionEnabled === false && (
            <MutationNotice tone="error" title="Run creation disabled">
              Fresh server readiness is incomplete. Complete or repair the
              workspace checklist before submitting a run.
            </MutationNotice>
          )}
          {error && (
            <MutationNotice tone="error" title="Run not created">
              {error}
            </MutationNotice>
          )}
          <button
            type="submit"
            className="button primary wide"
            disabled={!capability || !eventSinkId || !executionEnabled ||
              !goal.trim() ||
              (capability.capability_id === "ops.document.draft.v1" &&
                (!/^artifact:\/\/[A-Za-z0-9][A-Za-z0-9_-]*$/.test(sourceRef) ||
                  !/^[a-f0-9]{64}$/.test(sourceSha256))) ||
              submitState === "pending"}
          >
            {submitState === "pending"
              ? "Submitting for acknowledgement…"
              : "Create run"}
          </button>
          <p className="fineprint">
            The run becomes visible only after a 202 acknowledgement with a
            server-generated run ID.
          </p>
        </aside>
      </form>
    </>
  );
}

function RunDetail() {
  const runtime = useRuntime();
  const { runId } = useParams();
  const [run, setRun] = useState<RunResponse | null>(null);
  const [events, setEvents] = useState<RunEventsResponse["events"]>([]);
  const [approvalEvidence, setApprovalEvidence] = useState<ApprovalRequestedPayload | null>(null);
  const [artifacts, setArtifacts] = useState<
    Array<{
      ref: string;
      metadata?: ArtifactMetadata;
      error?: string;
    }>
  >([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [error, setError] = useState("");
  const [cancelState, setCancelState] = useState<
    "idle" | "confirm" | "pending" | "success" | "error"
  >("idle");
  const cancelKey = useRef(createIdempotencyKey());
  const [decisionAcknowledged, setDecisionAcknowledged] = useState(false);
  const [downloadState, setDownloadState] = useState<
    Record<string, "idle" | "pending" | "success" | "error">
  >({});
  const [downloadError, setDownloadError] = useState<Record<string, string>>(
    {},
  );
  async function load() {
    if (!runId) return;
    setLoadState("loading");
    setApprovalEvidence(null);
    try {
      const [runResult, eventResult] = await Promise.all([
        readApi<RunResponse>(`/runs/${encodeURIComponent(runId)}`, {
          tenantId: runtime.membership.tenant_id,
        }),
        readApi<RunEventsResponse>(
          `/runs/${encodeURIComponent(runId)}/events`,
          { tenantId: runtime.membership.tenant_id },
        ),
      ]);
      if (
        runResult.run_id !== runId ||
        runResult.tenant_id !== runtime.membership.tenant_id ||
        runResult.workspace_id !== runtime.workspaceId ||
        !eventsMatchRunBoundary(eventResult.events, {
          tenantId: runtime.membership.tenant_id,
          workspaceId: runtime.workspaceId,
          runId: runResult.run_id,
        })
      ) throw new Error("Run evidence did not match the selected boundary");
      const verifiedApproval = runResult.approval_id
        ? await verifiedApprovalRequestForRun(eventResult.events, runResult.approval_id, runResult)
        : null;
      setRun(runResult);
      setEvents(eventResult.events);
      setApprovalEvidence(verifiedApproval);
      const evidence = artifactEvidenceFrom(eventResult.events);
      const refs = [
        ...new Set([
          ...(runResult.artifact_refs ?? []),
          ...(runResult.evidence_refs ?? []),
          ...evidence.map((item) => item.artifact_ref),
        ]),
      ];
      const metadata = await Promise.all(refs.map(async (ref) => {
        const artifactId = artifactIdFromRef(ref);
        if (!artifactId) return { ref, error: "Invalid artifact reference" };
        try {
          const result = await readApi<ArtifactMetadata>(
            `/artifacts/${encodeURIComponent(artifactId)}`,
            { tenantId: runtime.membership.tenant_id },
          );
          const expectedHref =
            `/api/carbon-operations/v1/artifacts/${artifactId}/content`;
          if (
            result.schema !== "carbon.operations.artifact/v1" ||
            result.artifact_id !== artifactId ||
            result.run_id !== runResult.run_id ||
            result.workspace_id !== runResult.workspace_id ||
            result.kind !== "document" ||
            result.media_type !== "text/markdown" ||
            !Number.isInteger(result.byte_length) ||
            result.byte_length < 0 ||
            result.byte_length > 10 * 1_024 * 1_024 ||
            !/^[a-f0-9]{64}$/.test(result.sha256) ||
            !Number.isFinite(Date.parse(result.created_at)) ||
            result.content_href !== expectedHref ||
            result.immutable !== true
          ) throw new Error("Artifact metadata did not match the run boundary");
          const recordedEvidence = evidence.filter((item) =>
            item.artifact_ref === ref
          );
          if (
            recordedEvidence.some((item) =>
              item.artifact_id !== result.artifact_id ||
              item.kind !== result.kind ||
              item.media_type !== result.media_type ||
              item.byte_length !== result.byte_length ||
              item.sha256 !== result.sha256 ||
              item.immutable !== result.immutable ||
              item.created_at !== result.created_at
            )
          ) throw new Error("Artifact metadata did not match ordered evidence");
          return { ref, metadata: result };
        } catch (cause) {
          return {
            ref,
            error: cause instanceof Error
              ? cause.message
              : "Artifact metadata unavailable",
          };
        }
      }));
      setArtifacts(metadata);
      setLoadState("ready");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Run unavailable");
      setLoadState("error");
    }
  }
  useEffect(() => {
    void load();
  }, [runId, runtime.membership.tenant_id]);
  async function cancel() {
    if (!run || !hasAllScopes(runtime.membership, ["runs:cancel"])) return;
    const request: RunCancel = {
      schema: "carbon.operations.run.cancel/v1",
      reason: "Canceled from the workspace console",
    };
    setCancelState("pending");
    try {
      await acknowledgedMutation(
        `/runs/${run.run_id}/cancel`,
        request,
        runtime.boundary(cancelKey.current),
      );
      setCancelState("success");
      await load();
    } catch {
      setCancelState("error");
    }
  }
  async function downloadArtifact(metadata: ArtifactMetadata) {
    setDownloadError((current) => ({
      ...current,
      [metadata.artifact_id]: "",
    }));
    setDownloadState((current) => ({
      ...current,
      [metadata.artifact_id]: "pending",
    }));
    try {
      const expectedHref =
        `/api/carbon-operations/v1/artifacts/${metadata.artifact_id}/content`;
      if (metadata.content_href !== expectedHref) {
        throw new Error("Artifact content route did not match its identity");
      }
      const content = await readApiBytes(metadata.content_href, {
        tenantId: runtime.membership.tenant_id,
      });
      if (
        content.bytes.byteLength !== metadata.byte_length ||
        content.mediaType !== metadata.media_type ||
        await sha256Hex(content.bytes) !== metadata.sha256
      ) throw new Error(ArtifactIntegrityCopy.metadataMismatch);
      const url = URL.createObjectURL(
        new Blob([content.bytes], { type: metadata.media_type }),
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${metadata.artifact_id}.md`;
      anchor.click();
      URL.revokeObjectURL(url);
      setDownloadState((current) => ({
        ...current,
        [metadata.artifact_id]: "success",
      }));
    } catch (cause) {
      setDownloadError((current) => ({
        ...current,
        [metadata.artifact_id]: cause instanceof Error
          ? cause.message
          : "Artifact content unavailable",
      }));
      setDownloadState((current) => ({
        ...current,
        [metadata.artifact_id]: "error",
      }));
    }
  }
  if (loadState === "loading") {
    return (
      <>
        <PageHeader
          eyebrow="Runs / Detail"
          title="Loading run"
          description="Reading the acknowledged run and ordered events…"
        />
        <MutationNotice title="Run pending">
          The console has not inferred a state.
        </MutationNotice>
      </>
    );
  }
  if (loadState === "error" || !run) {
    return (
      <>
        <PageHeader
          eyebrow="Runs / Detail"
          title="Run unavailable"
          description="No cross-tenant existence information is shown."
        />
        <MutationNotice tone="error" title="Unable to open run">
          {error}
        </MutationNotice>
      </>
    );
  }
  const canApprove = hasAnyRole(runtime.membership, [
    "owner",
    "admin",
    "approver",
  ]) && hasAllScopes(runtime.membership, ["approvals:decide"]);
  const canCancel = hasAllScopes(runtime.membership, ["runs:cancel"]);
  const artifactEvidence = artifactEvidenceFrom(events);
  return (
    <>
      <PageHeader
        eyebrow={`Runs / ${run.run_id}`}
        title={run.run_id}
        description={`Acknowledged run · updated ${
          run.updated_at ?? run.created_at
        }`}
        action={
          <div className="page-actions">
            <Status tone={stateTone(run.state)}>{stateLabel(run.state)}</Status>
            <button
              type="button"
              className="button ghost"
              onClick={() => void load()}
            >
              Refresh run
            </button>
          </div>
        }
      />
      <div className="detail-grid">
        <div className="stack">
          <Panel title="Ordered events">
            <div className="timeline">
              {events.map((event) => (
                <article key={event.event_id}>
                  <time>{String(event.sequence).padStart(3, "0")}</time>
                  <span>{event.sequence}</span>
                  <div>
                    <strong>{event.type}</strong>
                    <p>{new Date(event.occurred_at).toLocaleString()}</p>
                    <code>{event.event_id}</code>
                  </div>
                </article>
              ))}
            </div>
          </Panel>
          {run.state === "awaiting_approval" && (
            <Panel title="Exact-action approval">
              {!canApprove
                ? (
                  <MutationNotice tone="error" title="Decision unavailable">
                    The active membership lacks an eligible role or the
                    approvals:decide scope.
                  </MutationNotice>
                )
                : !approvalEvidence
                ? (
                  <MutationNotice tone="error" title="Decision disabled">
                    The ordered event stream did not provide a matching valid
                    approval ID, exact-action digest, reason and expiry.
                  </MutationNotice>
                )
                : (
                  <ExactActionDecisionForm
                    key={approvalEvidence.approval_id}
                    approval={approvalEvidence}
                    onDecided={async () => {
                      setDecisionAcknowledged(true);
                      await load();
                    }}
                  />
                )}
            </Panel>
          )}
          {decisionAcknowledged && (
            <MutationNotice tone="success" title="Decision acknowledged">
              The run and ordered events were refreshed from the control plane.
            </MutationNotice>
          )}
          <Panel title={ArtifactIntegrityCopy.panelTitle}>
            <div className="prose">
              <p>{ArtifactIntegrityCopy.providerBoundary}</p>
              {artifacts.length === 0
                ? (
                  <p>
                    No artifact or evidence reference has been returned yet.
                  </p>
                )
                : (
                  <div className="artifact-list">
                    {artifacts.map((artifact) => (
                      <article className="artifact-card" key={artifact.ref}>
                        <code>{artifact.ref}</code>
                        {artifact.metadata
                          ? (
                            <>
                              <Definition
                                term="Kind / media"
                                value={`${artifact.metadata.kind} · ${artifact.metadata.media_type}`}
                              />
                              <Definition
                                term="Bytes"
                                value={artifact.metadata.byte_length
                                  .toLocaleString()}
                              />
                              <Definition
                                term="SHA-256"
                                value={artifact.metadata.sha256}
                              />
                              <Definition
                                term="Created"
                                value={new Date(artifact.metadata.created_at)
                                  .toLocaleString()}
                              />
                              <Definition
                                term="Integrity"
                                value={artifact.metadata.immutable
                                  ? ArtifactIntegrityCopy.verifiedState
                                  : "Unverified"}
                              />
                              <button
                                type="button"
                                className="button ghost"
                                disabled={downloadState[
                                  artifact.metadata
                                    .artifact_id
                                ] === "pending"}
                                onClick={() =>
                                  void downloadArtifact(artifact.metadata!)}
                              >
                                {downloadState[
                                    artifact.metadata.artifact_id
                                  ] ===
                                    "pending"
                                  ? "Verifying download…"
                                  : "Download verified content"}
                              </button>
                              {downloadState[artifact.metadata.artifact_id] ===
                                  "success" && (
                                <MutationNotice
                                  tone="success"
                                  title="Download verified"
                                >
                                  {ArtifactIntegrityCopy.downloadVerified}
                                </MutationNotice>
                              )}
                              {downloadState[artifact.metadata.artifact_id] ===
                                  "error" && (
                                <MutationNotice
                                  tone="error"
                                  title="Download blocked"
                                >
                                  {downloadError[
                                    artifact.metadata.artifact_id
                                  ] ??
                                    "Artifact content unavailable"}
                                </MutationNotice>
                              )}
                            </>
                          )
                          : (
                            <MutationNotice
                              tone="error"
                              title="Metadata unavailable"
                            >
                              {artifact.error ?? "Artifact unavailable"}
                            </MutationNotice>
                          )}
                      </article>
                    ))}
                  </div>
                )}
              {artifactEvidence.length > 0 && (
                <div className="evidence-ledger">
                  <h3>Readback evidence</h3>
                  {artifactEvidence.map((item) => (
                    <div key={item.artifact_id}>
                      <code>{item.sha256}</code>
                      <p>
                        {item.template_version} · runner {item.runner_id}{" "}
                        · readback {new Date(item.readback_verified_at)
                          .toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Panel>
        </div>
        <aside className="stack">
          <Panel title="Run boundary">
            <div className="prose">
              <Definition term="Tenant" value={run.tenant_id} />
              <Definition term="Workspace" value={run.workspace_id} />
              <Definition term="Risk" value={stateLabel(run.risk_class)} />
              <Definition
                term="Policy"
                value={run.policy_decision ?? "Pending"}
              />
              <Definition
                term="Approval"
                value={run.approval_id ?? "Not required"}
              />
              {run.approval_id && canApprove && (
                <p>
                  <Link
                    className="strong-link"
                    to={`${runtime.base}/approvals/${
                      encodeURIComponent(run.approval_id)
                    }`}
                  >
                    Open approval record
                  </Link>
                </p>
              )}
            </div>
          </Panel>
          <Panel title="Controls">
            <div className="control-block">
              <p>
                Cancellation is confirmed only after the server acknowledges the
                exact run mutation.
              </p>
              {cancelState === "idle" && [
                "queued",
                "awaiting_approval",
                "running",
              ].includes(run.state) && canCancel && (
                <button
                  type="button"
                  className="button danger"
                  onClick={() => setCancelState("confirm")}
                >
                  Cancel run
                </button>
              )}
              {cancelState === "idle" && ![
                "queued",
                "awaiting_approval",
                "running",
              ].includes(run.state) && (
                <p>This run is terminal or already canceling.</p>
              )}
              {cancelState === "idle" && [
                "queued",
                "awaiting_approval",
                "running",
              ].includes(run.state) && !canCancel && (
                <p>The active membership does not include runs:cancel.</p>
              )}
              {cancelState === "confirm" && (
                <div className="confirm-box">
                  <strong>Cancel {run.run_id}?</strong>
                  <p>Retained events and evidence remain available.</p>
                  <div>
                    <button
                      type="button"
                      className="button danger"
                      onClick={() => void cancel()}
                    >
                      Confirm cancellation
                    </button>
                    <button
                      type="button"
                      className="button ghost"
                      onClick={() => setCancelState("idle")}
                    >
                      Keep running
                    </button>
                  </div>
                </div>
              )}
              {cancelState === "pending" && (
                <MutationNotice title="Cancellation pending">
                  Waiting for control-plane acknowledgement…
                </MutationNotice>
              )}
              {cancelState === "success" && (
                <MutationNotice
                  tone="success"
                  title="Cancellation acknowledged"
                >
                  The server returned the updated run boundary.
                </MutationNotice>
              )}
              {cancelState === "error" && (
                <MutationNotice tone="error" title="Run unchanged">
                  Cancellation was not acknowledged.
                </MutationNotice>
              )}
            </div>
          </Panel>
        </aside>
      </div>
    </>
  );
}

function Readiness() {
  const runtime = useRuntime();
  const canRead = hasAllScopes(runtime.membership, ["readiness:read"]);
  const canConfigure = hasAnyRole(runtime.membership, ["owner", "admin"]) &&
    hasAllScopes(runtime.membership, ["readiness:write"]);
  const [readiness, setReadiness] = useState<WorkspaceReadiness | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [mutationState, setMutationState] = useState<
    | "idle"
    | "saving"
    | "verifying"
    | "success"
    | "blocked"
    | "timeout"
    | "saveError"
    | "verifyError"
  >("idle");
  const [error, setError] = useState("");
  const [purpose, setPurpose] = useState("");
  const [classification, setClassification] = useState<
    WorkspaceReadinessConfigure["data_classification"]
  >("confidential");
  const [custodian, setCustodian] = useState(
    runtime.session.identity.subject_id,
  );
  const [providerLabel, setProviderLabel] = useState("");
  const [providerScope, setProviderScope] = useState("");
  const [providerPurpose, setProviderPurpose] = useState("");
  const [reviewAt, setReviewAt] = useState(() =>
    toLocalDateTimeValue(new Date(Date.now() + 30 * 86_400_000).toISOString())
  );
  const [expiresAt, setExpiresAt] = useState(() =>
    toLocalDateTimeValue(new Date(Date.now() + 90 * 86_400_000).toISOString())
  );
  const [currency, setCurrency] = useState("USD");
  const [amountMinor, setAmountMinor] = useState("");
  const [retentionDays, setRetentionDays] = useState("365");
  const [confirmed, setConfirmed] = useState(false);
  const configurationIntent = useRef({
    signature: "",
    key: createIdempotencyKey(),
  });
  const mutationLock = useRef<ReadinessMutationLock>({ inFlight: false });
  const verificationController = useRef<AbortController | null>(null);
  const scopeKey = `${runtime.membership.tenant_id}/${runtime.workspaceId}`;
  const activeScope = useRef(scopeKey);
  activeScope.current = scopeKey;

  function applyConfiguration(response: WorkspaceReadiness) {
    if (!response.configuration) return;
    setPurpose(response.configuration.workspace_purpose);
    setClassification(response.configuration.data_classification);
    setCustodian(response.configuration.provider_custodian.subject_id);
    setProviderLabel(
      response.configuration.provider_custodian.provider_label,
    );
    setProviderScope(response.configuration.provider_custodian.scope);
    setProviderPurpose(response.configuration.provider_custodian.purpose);
    setReviewAt(
      toLocalDateTimeValue(response.configuration.provider_custodian.review_at),
    );
    setExpiresAt(
      toLocalDateTimeValue(
        response.configuration.provider_custodian.expires_at,
      ),
    );
    setCurrency(response.configuration.budget_threshold.currency);
    setAmountMinor(
      String(response.configuration.budget_threshold.amount_minor),
    );
    setRetentionDays(
      String(response.configuration.artifact_retention_days),
    );
    setConfirmed(false);
  }

  async function loadReadiness(
    syncConfiguration = false,
    signal?: AbortSignal,
  ) {
    if (!canRead) {
      setLoadState("error");
      setError("The active membership does not include readiness:read.");
      return;
    }
    setLoadState("loading");
    setError("");
    try {
      const response = await readApi<WorkspaceReadiness>(
        `/workspaces/${encodeURIComponent(runtime.workspaceId)}/readiness`,
        {
          tenantId: runtime.membership.tenant_id,
          ...(signal ? { signal } : {}),
        },
      );
      if (
        !readinessMatchesBoundary(response, {
          tenantId: runtime.membership.tenant_id,
          workspaceId: runtime.workspaceId,
        })
      ) throw new Error("Readiness response did not match the selected scope");
      setReadiness(response);
      if (syncConfiguration) applyConfiguration(response);
      setLoadState("ready");
    } catch (cause) {
      if (signal?.aborted) return;
      setError(
        cause instanceof Error ? cause.message : "Readiness state unavailable",
      );
      setLoadState("error");
    }
  }
  useEffect(() => {
    const loadController = new AbortController();
    verificationController.current?.abort();
    verificationController.current = null;
    setMutationState("idle");
    setError("");
    void loadReadiness(true, loadController.signal);
    return () => {
      loadController.abort();
      verificationController.current?.abort();
    };
  }, [runtime.workspaceId, runtime.membership.tenant_id, canRead]);

  function changed() {
    verificationController.current?.abort();
    setMutationState("idle");
    setError("");
  }
  async function configure(event: FormEvent) {
    event.preventDefault();
    if (!readiness || !canConfigure || !formValid) return;
    const reviewIso = localDateTimeToIso(reviewAt);
    const expiryIso = localDateTimeToIso(expiresAt);
    const amount = Number(amountMinor);
    const retention = Number(retentionDays);
    if (!reviewIso || !expiryIso) return;
    const request: WorkspaceReadinessConfigure = {
      schema: "carbon.operations.workspace-readiness.configure/v1",
      expected_revision: readiness.revision,
      workspace_purpose: purpose.trim(),
      data_classification: classification,
      provider_custodian: {
        subject_id: custodian.trim(),
        provider_label: providerLabel.trim(),
        scope: providerScope.trim(),
        purpose: providerPurpose.trim(),
        expires_at: expiryIso,
        review_at: reviewIso,
      },
      budget_threshold: {
        currency: currency.trim(),
        amount_minor: amount,
        period: "monthly",
      },
      artifact_retention_days: retention,
      confirmation: true,
    };
    const signature = JSON.stringify(request);
    if (configurationIntent.current.signature !== signature) {
      configurationIntent.current = {
        signature,
        key: createIdempotencyKey(),
      };
    }
    await runSingleReadinessMutation(mutationLock.current, async () => {
      const operationScope = scopeKey;
      const controller = new AbortController();
      verificationController.current?.abort();
      verificationController.current = controller;
      setMutationState("saving");
      setError("");
      try {
        const response = await mutationApi<WorkspaceReadiness>(
          `/workspaces/${
            encodeURIComponent(runtime.workspaceId)
          }/readiness/configuration`,
          request,
          {
            ...runtime.boundary(configurationIntent.current.key),
            signal: controller.signal,
          },
        );
        if (
          controller.signal.aborted || activeScope.current !== operationScope
        ) return;
        if (
          !readinessMatchesBoundary(response, {
            tenantId: runtime.membership.tenant_id,
            workspaceId: runtime.workspaceId,
          })
        ) {
          throw new Error(
            "Readiness response did not match the selected scope",
          );
        }
        setReadiness(response);
        setLoadState("ready");
        setConfirmed(false);
        setMutationState("verifying");

        const outcome = await verifyQueuedReadiness({
          initial: response,
          signal: controller.signal,
          read: async (signal) => {
            const next = await readApi<WorkspaceReadiness>(
              `/workspaces/${
                encodeURIComponent(runtime.workspaceId)
              }/readiness`,
              { tenantId: runtime.membership.tenant_id, signal },
            );
            if (
              !readinessMatchesBoundary(next, {
                tenantId: runtime.membership.tenant_id,
                workspaceId: runtime.workspaceId,
              })
            ) {
              throw new Error(
                "Readiness response did not match the selected scope",
              );
            }
            return next;
          },
          onUpdate: (next) => {
            if (
              !controller.signal.aborted &&
              activeScope.current === operationScope
            ) setReadiness(next);
          },
        });
        if (
          controller.signal.aborted || activeScope.current !== operationScope
        ) return;
        setReadiness(outcome.readiness);
        if (outcome.kind === "ready") {
          setMutationState("success");
        } else if (outcome.kind === "blocked") {
          setError(outcome.detail);
          setMutationState("blocked");
        } else if (outcome.kind === "timeout") {
          setMutationState("timeout");
        } else if (outcome.kind === "error") {
          setError(outcome.error.message);
          setMutationState("verifyError");
        }
      } catch (cause) {
        if (
          controller.signal.aborted || activeScope.current !== operationScope
        ) return;
        setError(
          cause instanceof Error
            ? cause.message
            : "Readiness configuration was not accepted",
        );
        setMutationState("saveError");
      } finally {
        if (verificationController.current === controller) {
          verificationController.current = null;
        }
      }
    });
  }

  const reviewIso = localDateTimeToIso(reviewAt);
  const expiryIso = localDateTimeToIso(expiresAt);
  const formValid = purpose.trim().length > 0 &&
    custodian.trim().length > 0 && providerLabel.trim().length > 0 &&
    providerScope.trim().length > 0 && providerPurpose.trim().length > 0 &&
    reviewIso !== null && expiryIso !== null &&
    Date.parse(reviewIso) > Date.now() &&
    Date.parse(expiryIso) > Date.parse(reviewIso) &&
    /^[A-Z]{3}$/.test(currency) && Number.isInteger(Number(amountMinor)) &&
    Number(amountMinor) > 0 && Number.isInteger(Number(retentionDays)) &&
    Number(retentionDays) >= 1 && Number(retentionDays) <= 3_650 && confirmed;
  const satisfied =
    readiness?.checks.filter((item) => item.status === "satisfied").length ?? 0;
  const canaryDetail = readiness?.checks.find((item) =>
    item.key === "canary_probe"
  )?.detail;
  const mutationBusy = mutationState === "saving" ||
    mutationState === "verifying";
  return (
    <>
      <PageHeader
        eyebrow="First run / Required controls"
        title="Workspace readiness"
        description="Configure the human-owned controls once; infrastructure, approval and canary checks remain server-derived."
      />
      {loadState === "loading" && (
        <MutationNotice title="Readiness pending">
          Reading the server-owned workspace gate…
        </MutationNotice>
      )}
      {loadState === "error" && (
        <MutationNotice tone="error" title="Readiness unavailable">
          {error}
        </MutationNotice>
      )}
      {mutationState === "saving" && (
        <MutationNotice title="Saving owner configuration">
          The server is validating this revision. No verification request will
          be repeated while it is in flight.
        </MutationNotice>
      )}
      {mutationState === "verifying" && (
        <MutationNotice title="Verification queued · execution disabled">
          {canaryDetail ??
            "The server is completing the isolated object-store readiness probe."}
          {" "}
          This page is checking the current server state for a bounded period.
        </MutationNotice>
      )}
      {readiness && (
        <div className="readiness-layout">
          <aside className="readiness-score">
            <span>{satisfied}/{readiness.checks.length}</span>
            <strong>
              {readiness.state === "ready" ? "Ready" : "Incomplete"}
            </strong>
            <p>
              {readiness.execution_enabled
                ? "The server has enabled execution."
                : "Execution remains disabled by the server."}
            </p>
          </aside>
          <section className="checklist">
            {readiness.checks.map((item, index) => (
              <article className={item.status} key={item.key}>
                <span>{index + 1}</span>
                <div>
                  <strong>{readinessLabel(item.key)}</strong>
                  <p>{item.detail}</p>
                </div>
                <Status
                  tone={item.status === "satisfied"
                    ? "success"
                    : item.status === "blocked"
                    ? "danger"
                    : "attention"}
                >
                  {stateLabel(item.status)} ·{" "}
                  {item.source === "system" ? "Server" : "Owner"}
                </Status>
              </article>
            ))}
          </section>
        </div>
      )}
      {readiness && (
        <div className="readiness-actions">
          <button
            type="button"
            className="button ghost"
            disabled={loadState === "loading" || mutationBusy}
            onClick={() => {
              setMutationState("idle");
              setError("");
              void loadReadiness(false);
            }}
          >
            {loadState === "loading" ? "Refreshing…" : "Refresh readiness"}
          </button>
          <span>
            Refresh reads the current server state; it does not resubmit owner
            configuration.
          </span>
        </div>
      )}
      {readiness && mutationState === "success" && (
        <MutationNotice
          tone="success"
          title={readiness.execution_enabled
            ? "Workspace ready"
            : "Configuration acknowledged · checks incomplete"}
        >
          {readiness.execution_enabled
            ? "The fresh server response enables run creation."
            : "The configuration was persisted. Server-derived checks still block execution."}
        </MutationNotice>
      )}
      {readiness && mutationState === "blocked" && (
        <MutationNotice tone="error" title="Verification blocked">
          {error}{" "}
          Refresh to read the current server state, or correct the reported
          condition and explicitly save again to retry verification.
        </MutationNotice>
      )}
      {readiness && mutationState === "timeout" && (
        <MutationNotice title="Verification still pending">
          Automatic checks stopped after the bounded wait. Execution remains
          disabled. Refresh to read the current server state; refreshing will
          not submit the configuration again.
        </MutationNotice>
      )}
      {readiness && mutationState === "saveError" && (
        <MutationNotice tone="error" title="Configuration unchanged">
          {error}{" "}
          Review the reported condition and save again to submit a new request.
        </MutationNotice>
      )}
      {readiness && mutationState === "verifyError" && (
        <MutationNotice tone="error" title="Verification stopped">
          {error}{" "}
          Refresh to retry the status read without repeating the owner
          configuration mutation.
        </MutationNotice>
      )}
      {readiness && canConfigure && (
        <form className="readiness-form panel" onSubmit={configure}>
          <header>
            <h2>Owner configuration</h2>
          </header>
          <fieldset className="readiness-fields" disabled={mutationBusy}>
            <label className="field field-wide">
              <span>Workspace purpose</span>
              <textarea
                required
                rows={3}
                maxLength={1_000}
                value={purpose}
                onChange={(event) => {
                  changed();
                  setPurpose(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Data classification</span>
              <select
                value={classification}
                onChange={(event) => {
                  changed();
                  setClassification(
                    event.target.value as typeof classification,
                  );
                }}
              >
                <option value="internal">Internal</option>
                <option value="confidential">Confidential</option>
                <option value="restricted">Restricted</option>
              </select>
            </label>
            <label className="field">
              <span>Custodian subject ID</span>
              <input
                required
                value={custodian}
                onChange={(event) => {
                  changed();
                  setCustodian(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Provider label</span>
              <input
                required
                maxLength={120}
                value={providerLabel}
                onChange={(event) => {
                  changed();
                  setProviderLabel(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Provider scope</span>
              <input
                required
                maxLength={500}
                value={providerScope}
                onChange={(event) => {
                  changed();
                  setProviderScope(event.target.value);
                }}
              />
            </label>
            <label className="field field-wide">
              <span>Provider purpose</span>
              <input
                required
                maxLength={500}
                value={providerPurpose}
                onChange={(event) => {
                  changed();
                  setProviderPurpose(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Review date</span>
              <input
                required
                type="datetime-local"
                value={reviewAt}
                onChange={(event) => {
                  changed();
                  setReviewAt(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Custody expiry</span>
              <input
                required
                type="datetime-local"
                value={expiresAt}
                min={reviewAt}
                onChange={(event) => {
                  changed();
                  setExpiresAt(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Budget currency</span>
              <input
                required
                pattern="[A-Z]{3}"
                maxLength={3}
                value={currency}
                onChange={(event) => {
                  changed();
                  setCurrency(event.target.value.toUpperCase());
                }}
              />
            </label>
            <label className="field">
              <span>Monthly limit (minor units)</span>
              <input
                required
                type="number"
                min={1}
                step={1}
                value={amountMinor}
                onChange={(event) => {
                  changed();
                  setAmountMinor(event.target.value);
                }}
              />
            </label>
            <label className="field">
              <span>Artifact retention (days)</span>
              <input
                required
                type="number"
                min={1}
                max={3_650}
                step={1}
                value={retentionDays}
                onChange={(event) => {
                  changed();
                  setRetentionDays(event.target.value);
                }}
              />
            </label>
            <label className="confirmation field-wide">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(event) => {
                  changed();
                  setConfirmed(event.target.checked);
                }}
              />
              <span>
                I confirm the named custodian, purpose, scope, review/expiry,
                budget and retention boundary for this workspace.
              </span>
            </label>
          </fieldset>
          <button
            type="submit"
            className="button primary"
            disabled={!formValid || mutationBusy}
          >
            {mutationState === "saving"
              ? "Saving owner configuration…"
              : mutationState === "verifying"
              ? "Verification in progress…"
              : ["blocked", "timeout", "saveError", "verifyError"].includes(
                  mutationState,
                )
              ? "Save and retry verification"
              : "Save and verify readiness"}
          </button>
        </form>
      )}
      {readiness && !canConfigure && readiness.state !== "ready" && (
        <MutationNotice title="Owner action required">
          The active membership cannot configure readiness.
        </MutationNotice>
      )}
    </>
  );
}
function ExactActionDecisionForm(
  { approval, onDecided }: {
    approval: ApprovalRequestedPayload;
    onDecided: () => Promise<void>;
  },
) {
  const runtime = useRuntime();
  const [reason, setReason] = useState("");
  const [expiry, setExpiry] = useState(() =>
    toLocalDateTimeValue(approval.expires_at)
  );
  const [submitState, setSubmitState] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle");
  const [error, setError] = useState("");
  const intent = useRef({ signature: "", key: createIdempotencyKey() });
  async function decide(decision: "approve" | "deny") {
    const expiresAt = localDateTimeToIso(expiry);
    if (!expiresAt || reason.trim() === "") return;
    if (!await approvalActionContextDigestMatches(approval)) {
      setError("The displayed action context no longer matches the expected action SHA-256. No decision was sent.");
      setSubmitState("error");
      return;
    }
    const signature = JSON.stringify({
      approvalId: approval.approval_id,
      decision,
      reason: reason.trim(),
      expiresAt,
      expectedActionSha256: approval.expected_action_sha256,
    });
    if (intent.current.signature !== signature) {
      intent.current = { signature, key: createIdempotencyKey() };
    }
    setSubmitState("pending");
    setError("");
    try {
      await acknowledgedMutation(
        `/approvals/${encodeURIComponent(approval.approval_id)}/decisions`,
        buildApprovalDecision(
          decision,
          reason.trim(),
          approval.expected_action_sha256,
          expiresAt,
        ),
        runtime.boundary(intent.current.key),
      );
      setSubmitState("success");
      await onDecided();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Decision was not accepted",
      );
      setSubmitState("error");
    }
  }
  const decisionExpiry = localDateTimeToIso(expiry);
  const inputValid = decisionExpiry !== null &&
    Date.parse(decisionExpiry) > Date.now() &&
    Date.parse(decisionExpiry) <= Date.parse(approval.expires_at) &&
    reason.trim().length > 0 && reason.trim().length <= 1_000;
  return (
    <form
      className="approval-form"
      onSubmit={(event) => event.preventDefault()}
    >
      <p>
        Review the server-owned digest. The console recomputes it from the
        displayed action context and never accepts it from the URL.
      </p>
      <ApprovalActionContextFacts context={approval.action_context} />
      <Definition term="Approval ID" value={approval.approval_id} />
      <Definition term="Policy reason" value={approval.reason} />
      <Definition
        term="Server expiry"
        value={new Date(approval.expires_at).toLocaleString()}
      />
      <label className="field">
        <span>Expected action SHA-256</span>
        <textarea
          readOnly
          rows={2}
          value={approval.expected_action_sha256}
          aria-label="Expected action SHA-256 from ordered events"
        />
      </label>
      <label className="field">
        <span>Decision reason</span>
        <textarea
          required
          rows={3}
          maxLength={1_000}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
            setSubmitState("idle");
            setError("");
          }}
          placeholder="Explain why this exact action is approved or denied"
        />
      </label>
      <label className="field">
        <span>Decision expiry</span>
        <input
          required
          type="datetime-local"
          value={expiry}
          max={toLocalDateTimeValue(approval.expires_at)}
          onChange={(event) => {
            setExpiry(event.target.value);
            setSubmitState("idle");
            setError("");
          }}
        />
      </label>
      <div className="decision-actions">
        <button
          type="button"
          className="button primary"
          disabled={!inputValid || submitState === "pending"}
          onClick={() => void decide("approve")}
        >
          {submitState === "pending"
            ? "Submitting decision…"
            : "Approve exact action"}
        </button>
        <button
          type="button"
          className="button danger"
          disabled={!inputValid || submitState === "pending"}
          onClick={() => void decide("deny")}
        >
          Deny action
        </button>
      </div>
      {error && (
        <MutationNotice tone="error" title="Run unchanged">
          {error}
        </MutationNotice>
      )}
    </form>
  );
}

function ApprovalDetailPage() {
  const runtime = useRuntime();
  const { approvalId = "" } = useParams();
  const canDecide = hasAnyRole(runtime.membership, [
    "owner",
    "admin",
    "approver",
  ]) && hasAllScopes(runtime.membership, ["approvals:decide"]);
  const canReadRuns = hasAllScopes(runtime.membership, ["runs:read"]);
  const [detail, setDetail] = useState<ApprovalDetail | null>(null);
  const [evidence, setEvidence] = useState<ApprovalRequestedPayload | null>(
    null,
  );
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [error, setError] = useState("");
  const [decisionAcknowledged, setDecisionAcknowledged] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const loader = useRef(createLatestLoader());
  async function load() {
    const attempt = loader.current.begin();
    setLoadState("loading");
    try {
      const boundary = {
        tenantId: runtime.membership.tenant_id,
        signal: attempt.signal,
      };
      const scope = {
        tenantId: runtime.membership.tenant_id,
        workspaceId: runtime.workspaceId,
      };
      const result = await readApi<ApprovalDetail>(
        `/approvals/${encodeURIComponent(approvalId)}`,
        boundary,
      );
      if (!approvalMatchesBoundary(result, approvalId, scope)) {
        throw new Error("The approval did not match the selected workspace");
      }
      // The decision form needs the run's own ordered approval.requested event, never the URL or the list row.
      const matched = canReadRuns
        ? await verifiedApprovalDecisionEvidence(
          result,
          (await readApi<RunEventsResponse>(
            `/runs/${encodeURIComponent(result.run_id)}/events`,
            boundary,
          )).events,
          scope,
        )
        : null;
      if (!attempt.current()) return;
      setDetail(result);
      setEvidence(matched);
      setNowMs(Date.now());
      setLoadState("ready");
    } catch (cause) {
      if (!attempt.current()) return;
      setError(cause instanceof Error ? cause.message : "Approval unavailable");
      setLoadState("error");
    }
  }
  useEffect(() => {
    if (!canDecide) return;
    const current = loader.current;
    setDetail(null);
    setEvidence(null);
    setDecisionAcknowledged(false);
    void load();
    return () => current.cancel();
  }, [
    approvalId,
    canDecide,
    canReadRuns,
    runtime.membership.tenant_id,
    runtime.workspaceId,
  ]);
  if (!canDecide) {
    return (
      <>
        <PageHeader
          eyebrow="Governance / Exact action"
          title="Approval"
          description="One exact-action approval of this workspace, with its run and recorded decisions."
        />
        <Panel title="Approvals are decided by approvers">
          <div className="prose">
            <p>
              Only an owner, admin or approver of this workspace whose
              membership includes the approvals:decide scope can open an
              approval. Your role in this workspace does not, so nothing was
              requested.
            </p>
          </div>
        </Panel>
      </>
    );
  }
  if (loadState === "loading" && !detail) {
    return (
      <>
        <PageHeader
          eyebrow="Governance / Exact action"
          title="Loading approval"
          description="Reading the approval, its run and the run's ordered events…"
        />
        <MutationNotice title="Approval pending">
          No decision can be submitted until the server confirms the approval.
        </MutationNotice>
      </>
    );
  }
  if (loadState === "error" || !detail) {
    return (
      <>
        <PageHeader
          eyebrow="Governance / Exact action"
          title="Approval unavailable"
          description="Missing, other-workspace and own-request approvals look the same here; no existence information is shown."
        />
        <MutationNotice tone="error" title="Unable to open approval">
          {error}
        </MutationNotice>
      </>
    );
  }
  const unavailable = decisionUnavailableReason(detail, evidence, {
    decide: canDecide,
    readRuns: canReadRuns,
  }, nowMs);
  return (
    <>
      <PageHeader
        eyebrow={`Governance / ${detail.approval_id}`}
        title="Exact-action approval"
        description={`${detail.capability} · requested by ${detail.requested_by}`}
        action={
          <div className="page-actions">
            <Status
              tone={detail.state === "pending"
                ? "attention"
                : detail.state === "approved"
                ? "success"
                : "danger"}
            >
              {stateLabel(detail.state)}
            </Status>
            <button
              type="button"
              className="button ghost"
              disabled={loadState === "loading"}
              onClick={() => void load()}
            >
              Refresh approval
            </button>
          </div>
        }
      />
      <div className="detail-grid">
        <div className="stack">
          <Panel title="Requested action">
            <div className="prose">
              {evidence && <ApprovalActionContextFacts context={evidence.action_context} />}
              <Definition term="Capability" value={detail.capability} />
              <Definition term="Requested by" value={detail.requested_by} />
              <Definition
                term="Requested"
                value={new Date(detail.created_at).toLocaleString()}
              />
              <Definition
                term="Expires"
                value={new Date(detail.expires_at).toLocaleString()}
              />
              {detail.resolved_at && (
                <Definition
                  term="Resolved"
                  value={new Date(detail.resolved_at).toLocaleString()}
                />
              )}
              <Definition
                term="Required approvals"
                value={String(detail.required_approvals)}
              />
              <Definition
                term="Target"
                value={`${detail.target.type} ${detail.target.id} · version ${detail.target.version}`}
              />
              <Definition
                term="Expected action SHA-256"
                value={detail.expected_action_sha256}
              />
              <Definition term="Target SHA-256" value={detail.target.sha256} />
              {evidence && (
                <Definition term="Policy reason" value={evidence.reason} />
              )}
            </div>
          </Panel>
          <Panel title="Decision">
            {unavailable === null && evidence
              ? (
                <ExactActionDecisionForm
                  key={evidence.approval_id}
                  approval={evidence}
                  onDecided={async () => {
                    setDecisionAcknowledged(true);
                    await load();
                  }}
                />
              )
              : (
                <div className="prose">
                  <MutationNotice
                    tone={detail.state === "pending" ? "error" : "pending"}
                    title="Decision unavailable"
                  >
                    {unavailable}
                  </MutationNotice>
                </div>
              )}
          </Panel>
          {decisionAcknowledged && (
            <MutationNotice tone="success" title="Decision acknowledged">
              The approval and its run were refreshed from the control plane.
            </MutationNotice>
          )}
        </div>
        <aside className="stack">
          <Panel title="Recorded decisions">
            <div className="prose">
              {detail.decisions.length === 0
                ? <p>No decision has been recorded for this approval.</p>
                : detail.decisions.map((decision) => (
                  <div key={`${decision.decided_by}:${decision.decided_at}`}>
                    <Definition
                      term={stateLabel(decision.decision)}
                      value={`${decision.decided_by} · ${
                        new Date(decision.decided_at).toLocaleString()
                      }`}
                    />
                    {decision.reason && (
                      <Definition term="Reason" value={decision.reason} />
                    )}
                    <Definition
                      term="Decision SHA-256"
                      value={decision.decision_sha256}
                    />
                  </div>
                ))}
            </div>
          </Panel>
          <Panel title="Run">
            <div className="prose">
              <Definition term="Run" value={detail.run.run_id} />
              <Definition term="State" value={stateLabel(detail.run.state)} />
              <Definition term="Risk" value={stateLabel(detail.run.risk_class)} />
              {canReadRuns && (
                <p>
                  <Link
                    className="strong-link"
                    to={`${runtime.base}/runs/${
                      encodeURIComponent(detail.run.run_id)
                    }`}
                  >
                    Open run detail
                  </Link>
                </p>
              )}
            </div>
          </Panel>
        </aside>
      </div>
    </>
  );
}

/** Loads one read-only workspace record and refuses a response for any other tenant or workspace. */
function useWorkspaceRecord<
  T extends { schema: string; tenant_id: string; workspace_id: string },
>(page: string, schema: string, enabled: boolean) {
  const runtime = useRuntime();
  const [data, setData] = useState<T | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [error, setError] = useState("");
  const loader = useRef(createLatestLoader());
  async function load() {
    const attempt = loader.current.begin();
    setLoadState("loading");
    try {
      const result = await readApi<T>(
        `/workspaces/${encodeURIComponent(runtime.workspaceId)}/${page}`,
        { tenantId: runtime.membership.tenant_id, signal: attempt.signal },
      );
      if (!attempt.current()) return;
      if (
        !recordsMatchBoundary(result, schema, {
          tenantId: runtime.membership.tenant_id,
          workspaceId: runtime.workspaceId,
        })
      ) throw new Error("The response did not match the selected workspace");
      setData(result);
      setLoadState("ready");
    } catch (cause) {
      if (!attempt.current()) return;
      setError(cause instanceof Error ? cause.message : "Record unavailable");
      setLoadState("error");
    }
  }
  useEffect(() => {
    if (!enabled) return;
    const current = loader.current;
    setData(null);
    void load();
    return () => current.cancel();
  }, [enabled, page, runtime.membership.tenant_id, runtime.workspaceId]);
  return { data, loadState, error, reload: () => void load() };
}

function RecordState(
  { loadState, error, label }: {
    loadState: "loading" | "ready" | "error";
    error: string;
    label: string;
  },
) {
  if (loadState === "error") {
    return (
      <MutationNotice tone="error" title={`${label} unavailable`}>
        {error}
      </MutationNotice>
    );
  }
  return <p className="empty-state">Loading {label.toLowerCase()}…</p>;
}

function OperatorsPage() {
  const runtime = useRuntime();
  const access = recordsAccess(runtime.membership);
  const record = useWorkspaceRecord<WorkspaceOperatorsResponse>(
    "operators",
    "carbon.operations.workspace-operators/v1",
    access.operators,
  );
  return (
    <>
      <PageHeader
        eyebrow="Access / Workspace scoped"
        title="Operators"
        description="Everyone with a membership in this workspace, with their role, scopes and capability grants as the control plane records them."
        action={access.operators
          ? (
            <button
              type="button"
              className="button ghost"
              onClick={record.reload}
            >
              Refresh
            </button>
          )
          : null}
      />
      <p className="read-only-note">
        Read-only. Memberships, roles, scopes and capability grants change only
        through the reviewed client-admin provisioning path. This console
        cannot add, edit or revoke them.
      </p>
      {!access.operators
        ? (
          <Panel title="Operators need the readiness:read scope">
            <div className="prose">
              <p>
                Your active membership in this workspace does not include
                readiness:read, so the operator directory was not requested.
              </p>
            </div>
          </Panel>
        )
        : record.data
        ? <OperatorDirectory operators={record.data.operators} />
        : (
          <RecordState
            loadState={record.loadState}
            error={record.error}
            label="Operators"
          />
        )}
    </>
  );
}

function ConnectionsPage() {
  const runtime = useRuntime();
  const access = recordsAccess(runtime.membership);
  const record = useWorkspaceRecord<WorkspaceConnectionsResponse>(
    "connections",
    "carbon.operations.workspace-connections/v1",
    access.connections,
  );
  return (
    <>
      <PageHeader
        eyebrow="Custody / Least privilege"
        title="Connections"
        description="The event sinks, object store profiles and secret references this workspace's records use, with their status and history."
        action={access.connections
          ? (
            <button
              type="button"
              className="button ghost"
              onClick={record.reload}
            >
              Refresh
            </button>
          )
          : null}
      />
      <p className="read-only-note">
        Read-only. Secret values and their locators never leave the server, and
        an event sink shows only its destination origin. Rotation and
        revocation run through the security-admin runbook, not this console.
      </p>
      {!access.connections
        ? (
          <Panel title="Connections need the readiness:read scope">
            <div className="prose">
              <p>
                Your active membership in this workspace does not include
                readiness:read, so custody records were not requested.
              </p>
            </div>
          </Panel>
        )
        : record.data
        ? <ConnectionRecords connections={record.data} />
        : (
          <RecordState
            loadState={record.loadState}
            error={record.error}
            label="Connections"
          />
        )}
    </>
  );
}

const noAuditFilters: AuditFilters = {
  runId: "",
  eventType: "",
  from: "",
  to: "",
};

function AuditPage() {
  const runtime = useRuntime();
  const access = recordsAccess(runtime.membership);
  const [draft, setDraft] = useState<AuditFilters>(noAuditFilters);
  const [filters, setFilters] = useState<AuditFilters>(noAuditFilters);
  const [events, setEvents] = useState<AuditEventRecord[]>([]);
  const [chain, setChain] = useState<AuditListResponse["chain"] | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [error, setError] = useState("");
  const loader = useRef(createLatestLoader());
  async function load(after?: string) {
    const built = auditQuery(filters, after);
    if ("error" in built) {
      setError(built.error);
      setLoadState("error");
      return;
    }
    const attempt = loader.current.begin();
    setLoadState("loading");
    try {
      const result = await readApi<AuditListResponse>(
        `/workspaces/${
          encodeURIComponent(runtime.workspaceId)
        }/audit?${built.query}`,
        { tenantId: runtime.membership.tenant_id, signal: attempt.signal },
      );
      if (!attempt.current()) return;
      if (
        !recordsMatchBoundary(result, "carbon.operations.audit-events/v1", {
          tenantId: runtime.membership.tenant_id,
          workspaceId: runtime.workspaceId,
        })
      ) throw new Error("The response did not match the selected workspace");
      setEvents((previous) =>
        after === undefined ? result.events : [...previous, ...result.events]
      );
      setChain(result.chain);
      setCursor(result.next_cursor);
      setLoadState("ready");
    } catch (cause) {
      if (!attempt.current()) return;
      setError(cause instanceof Error ? cause.message : "Audit unavailable");
      setLoadState("error");
    }
  }
  useEffect(() => {
    if (!access.audit) return;
    const current = loader.current;
    setEvents([]);
    setCursor(undefined);
    void load();
    return () => current.cancel();
  }, [access.audit, filters, runtime.membership.tenant_id, runtime.workspaceId]);
  return (
    <>
      <PageHeader
        eyebrow={ArtifactIntegrityCopy.auditEyebrow}
        title="Audit"
        description="This workspace's events in the tenant's hash-chained audit ledger, newest first, each verified against the stored chain."
      />
      <p className="read-only-note">
        Read-only and append-only: nothing here can change or delete an event,
        and this console offers no export. Tenant-level events, such as
        sign-outs, belong to no workspace and are not shown.
      </p>
      {!access.audit
        ? (
          <Panel title="The audit ledger is for owners, admins and auditors">
            <div className="prose">
              <p>
                Reading the ledger needs an owner, admin or auditor role in this
                workspace and the runs:read scope. Your membership does not
                have both, so nothing was requested. Run events stay available
                on each run's detail.
              </p>
            </div>
          </Panel>
        )
        : (
          <Panel title="Audit ledger">
            <form
              className="records-toolbar"
              onSubmit={(event) => {
                event.preventDefault();
                setFilters({ ...draft });
              }}
            >
              <label className="field">
                <span>Run ID</span>
                <input
                  value={draft.runId}
                  spellCheck={false}
                  onChange={(event) =>
                    setDraft({ ...draft, runId: event.target.value })}
                />
              </label>
              <label className="field">
                <span>Event type</span>
                <input
                  list="audit-event-types"
                  value={draft.eventType}
                  spellCheck={false}
                  onChange={(event) =>
                    setDraft({ ...draft, eventType: event.target.value })}
                />
                <datalist id="audit-event-types">
                  {auditEventTypes.map((type) => (
                    <option key={type} value={type} />
                  ))}
                </datalist>
              </label>
              <label className="field">
                <span>From</span>
                <input
                  type="datetime-local"
                  value={draft.from}
                  onChange={(event) =>
                    setDraft({ ...draft, from: event.target.value })}
                />
              </label>
              <label className="field">
                <span>To</span>
                <input
                  type="datetime-local"
                  value={draft.to}
                  onChange={(event) =>
                    setDraft({ ...draft, to: event.target.value })}
                />
              </label>
              <div className="records-toolbar-actions">
                <button type="submit" className="button primary">
                  Apply
                </button>
                <button
                  type="button"
                  className="button ghost"
                  onClick={() => {
                    setDraft(noAuditFilters);
                    setFilters({ ...noAuditFilters });
                  }}
                >
                  Clear
                </button>
              </div>
            </form>
            {chain && <ChainStatus chain={chain} />}
            {loadState === "error"
              ? (
                <div className="prose">
                  <MutationNotice tone="error" title="Audit unavailable">
                    {error}
                  </MutationNotice>
                </div>
              )
              : loadState === "loading" && events.length === 0
              ? <p className="empty-state">Loading audit events…</p>
              : <AuditLedger events={events} base={runtime.base} />}
            {cursor !== undefined && loadState !== "error" && (
              <div className="records-more">
                <button
                  type="button"
                  className="button ghost"
                  disabled={loadState === "loading"}
                  onClick={() => void load(cursor)}
                >
                  Load older events
                </button>
              </div>
            )}
          </Panel>
        )}
    </>
  );
}

function SettingsPage() {
  const runtime = useRuntime();
  const access = recordsAccess(runtime.membership);
  const record = useWorkspaceRecord<WorkspaceSettingsResponse>(
    "settings",
    "carbon.operations.workspace-settings/v1",
    access.settings,
  );
  const settings = record.data;
  const configuration = settings?.configuration;
  return (
    <>
      <PageHeader
        eyebrow="Workspace / Control boundary"
        title="Settings"
        description="The workspace record, its stored approval policy and the owner configuration, exactly as the control plane holds them."
        action={access.settings
          ? (
            <button
              type="button"
              className="button ghost"
              onClick={record.reload}
            >
              Refresh
            </button>
          )
          : null}
      />
      <p className="read-only-note">
        Read-only. Owner configuration (purpose, custodian, budget and
        retention) is changed on the{" "}
        <Link className="strong-link" to={`${runtime.base}/readiness`}>
          Readiness
        </Link>{" "}
        page, which re-verifies the workspace. Policy fields change only through
        reviewed onboarding. Nothing on this page can be edited.
      </p>
      {!access.settings
        ? (
          <Panel title="Settings are for workspace owners and admins">
            <div className="prose">
              <p>
                Viewing settings needs an owner or admin role in this workspace
                and the readiness:read scope. Your membership does not have
                both, so nothing was requested.
              </p>
            </div>
          </Panel>
        )
        : !settings
        ? (
          <RecordState
            loadState={record.loadState}
            error={record.error}
            label="Settings"
          />
        )
        : (
          <div className="detail-grid">
            <div className="stack">
              <Panel title="Workspace">
                <div className="prose">
                  <Definition term="Display name" value={settings.display_name} />
                  <Definition term="Slug" value={settings.slug} />
                  <Definition term="Status" value={stateLabel(settings.status)} />
                  <Definition
                    term="Created"
                    value={new Date(settings.created_at).toLocaleString()}
                  />
                </div>
              </Panel>
              <Panel title="Approval policy (stored)">
                <div className="prose">
                  {policyEntries(settings.policy).length === 0
                    ? <p>No policy fields are stored for this workspace.</p>
                    : policyEntries(settings.policy).map((entry) => (
                      <Definition
                        key={entry.key}
                        term={entry.label}
                        value={entry.value}
                      />
                    ))}
                </div>
              </Panel>
            </div>
            <aside className="stack">
              <Panel title="Owner configuration">
                <div className="prose">
                  {!configuration
                    ? <p>No owner configuration has been recorded yet.</p>
                    : (
                      <>
                        <Definition
                          term="Purpose"
                          value={configuration.workspace_purpose}
                        />
                        <Definition
                          term="Data classification"
                          value={stateLabel(configuration.data_classification)}
                        />
                        <Definition
                          term="Provider custodian"
                          value={`${configuration.provider_custodian.provider_label} · ${configuration.provider_custodian.subject_id}`}
                        />
                        <Definition
                          term="Custody scope"
                          value={configuration.provider_custodian.scope}
                        />
                        <Definition
                          term="Custody purpose"
                          value={configuration.provider_custodian.purpose}
                        />
                        <Definition
                          term="Custody review / expiry"
                          value={`${
                            new Date(
                              configuration.provider_custodian.review_at,
                            ).toLocaleString()
                          } / ${
                            new Date(
                              configuration.provider_custodian.expires_at,
                            ).toLocaleString()
                          }`}
                        />
                        <Definition
                          term="Monthly budget"
                          value={`${configuration.budget_threshold.amount_minor.toLocaleString()} ${configuration.budget_threshold.currency} minor units`}
                        />
                        <Definition
                          term="Artifact retention"
                          value={`${configuration.artifact_retention_days} days`}
                        />
                      </>
                    )}
                </div>
              </Panel>
              <Panel title="Readiness record">
                <div className="prose">
                  <Definition
                    term="Configuration revision"
                    value={String(settings.readiness.revision)}
                  />
                  <Definition
                    term="Configured"
                    value={settings.readiness.configured_at
                      ? `${
                        new Date(settings.readiness.configured_at)
                          .toLocaleString()
                      }${
                        settings.readiness.configured_by
                          ? ` by ${settings.readiness.configured_by}`
                          : ""
                      }`
                      : "Not configured"}
                  />
                  <Definition
                    term="Last verified"
                    value={settings.readiness.last_verified_at
                      ? new Date(settings.readiness.last_verified_at)
                        .toLocaleString()
                      : "Not verified"}
                  />
                  <p>
                    <Link
                      className="strong-link"
                      to={`${runtime.base}/readiness`}
                    >
                      Open the live readiness gate
                    </Link>
                  </p>
                </div>
              </Panel>
            </aside>
          </div>
        )}
    </>
  );
}
function Unauthorized() {
  const runtime = useRuntime();
  return (
    <AccessState
      eyebrow="Scope denied"
      title="This tenant or workspace is not authorized"
      description="URL identifiers never grant authority. Choose only from memberships returned by the active server session."
      action="Return to authorized workspace"
      onAction={() => globalThis.location.assign(`${runtime.base}/overview`)}
    />
  );
}
function NotFound() {
  const runtime = useRuntime();
  return (
    <AccessState
      eyebrow="Not found"
      title="The requested control-plane view does not exist"
      description="Nothing was changed. Return to the authorized workspace overview."
      action="Open overview"
      onAction={() => globalThis.location.assign(`${runtime.base}/overview`)}
    />
  );
}
